/**
 * Public "Reported Concerns" map + accessible status list (public-concerns-map.md / ADR-0023).
 * Layout per the redesign spec (docs/design/concerns-map-redesign-spec.md, Sue's approved mockup:
 * https://claude.ai/code/artifact/d0009629-8892-4ee3-bc8d-49ee943ea4a2 — supersedes the original
 * mockup at .../121c82f9-35b4-4ee1-b7bc-f36e1c5487df). No auth, no PII — the backend's allowlist
 * projection (shared.PublicConcernPin) never carries reporter identity, exact address, or
 * description (NFR-3's same posture as /meetings and /track). Co-equal map + list, synced
 * bidirectionally (select a pin <-> highlight the row/card); side-by-side wide, stacked narrow via
 * CONTAINER QUERIES scoped to `.td-concerns__main` (app.css) — not viewport media queries, because
 * this page is also an embeddable widget (ADR-0004) that must respond to its embed width.
 *
 * NO-JS LIST: unlike /meetings and /code, this surface has NO prerender-on-publish path yet — the
 * backend PR (#416) shipped only the live GET /api/public/concerns Lambda, no companion static-HTML
 * generator (ADR-0017's Go emitter pattern exists for meetings/ordinances, not concerns). Since
 * apps/web is a client-only SPA (ADR-0010), there is currently NO way to serve this list's content
 * to a crawler or a JS-disabled browser from this repo alone — that gap is flagged in this PR's
 * description for Professor Pac/Lister; it needs a backend prerender step (or a /meetings-style SSR
 * decision) before the no-JS requirement is actually met. This page still renders a REAL semantic
 * `<table>` (caption/scope/tabular-nums) so it's fully keyboard/screen-reader accessible once JS has
 * run, and degrades to a clear "turn on JavaScript" message otherwise (see index.html's <noscript>).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listPublicConcerns } from "../api/index.js";
import type { PublicConcernPin, PublicConcernsView, PublicConcernStatus } from "../api/types.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { Skeleton, SkeletonRegion } from "../components/Skeleton.js";
import { ConcernsMap, type ConcernsMapHandle } from "../components/ConcernsMap.js";
import { ConcernStatusPill } from "../components/ConcernStatusPill.js";
import { shortDate } from "../components/format.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useEmbedResize } from "../components/useEmbedResize.js";

/** The three segments of the status switch (concerns-map-redesign-spec.md). "Active" is a
 *  CLIENT-SIDE union of open+in-progress — it never maps to a single `PublicConcernStatus` API
 *  param, so filtering for it happens entirely in `filterByStatusScope` below, against the
 *  already-fetched `view.pins` (no backend change, per the spec's recommended approach). */
type StatusScope = "active" | "resolved" | "all";
const DEFAULT_STATUS_SCOPE: StatusScope = "active";

function isActiveStatus(status: PublicConcernStatus): boolean {
  return status === "open" || status === "in-progress";
}

/** Filters a pin list to the given status scope — "unfiltered-of-this-dimension" relative to
 *  status itself (used both for the visible list/map pins AND for the switch's live counts). */
function filterByStatusScope(pins: PublicConcernPin[], scope: StatusScope): PublicConcernPin[] {
  if (scope === "all") return pins;
  if (scope === "resolved") return pins.filter((p) => p.status === "resolved");
  return pins.filter((p) => isActiveStatus(p.status));
}

/** PII-safe street-level location text (ADR-0023's two-tier location: fuzzed pin + this
 *  block-level text) — server-derived, NO house number. Rendered as "near {street}"; an empty/
 *  absent `location` (no street could be safely extracted) renders as an em-dash, never
 *  blank-with-no-context. Treat the value as untrusted (same posture as `category`) — React escapes
 *  it automatically since this is plain text content, never dangerouslySetInnerHTML. */
function approxLocation(pin: PublicConcernPin): string {
  return pin.location ? `near ${pin.location}` : "—";
}

interface ConcernsMapPageProps {
  embed?: boolean;
  chromeless?: boolean;
}

export function ConcernsMapPage({
  embed = false,
  chromeless = false,
}: ConcernsMapPageProps): JSX.Element {
  const { config } = useTheme();
  useDocumentTitle(pageTitle("Reported Concerns", config.townName));
  useEmbedClose(embed);
  const rootRef = useRef<HTMLDivElement>(null);
  useEmbedResize(embed, rootRef, "towndesk:concerns-resize");

  useEffect(() => {
    if (!chromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [chromeless]);

  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? "";
  // Status is now a 3-way CLIENT-SIDE scope (Active/Resolved/All), not a single API param — see
  // concerns-map-redesign-spec.md's "data/URL-param implication" note. The fetch never sends a
  // `status` filter; `filterByStatusScope` narrows the already-fetched `view.pins` below.
  const statusScopeParam = params.get("status");
  const statusScope: StatusScope =
    statusScopeParam === "resolved" || statusScopeParam === "all"
      ? statusScopeParam
      : DEFAULT_STATUS_SCOPE;
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const [view, setView] = useState<PublicConcernsView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);

  // STACKED-LAYOUT TAB VIEW ONLY (<1100px container width — the `<720` cards and `720-1099` table
  // cases; see app.css's `@container td-concerns` rules). At >=1100px the board is side-by-side and
  // this state is simply unused (both panels render via `.td-concerns__board`'s own CSS, no tabs in
  // the DOM for that width). Default is "map" per the redesign ask (land on the map, not the list).
  const [activeTab, setActiveTab] = useState<"map" | "list">("map");
  const mapHandleRef = useRef<ConcernsMapHandle>(null);

  // MapLibre measures its container once at construction time — a map built/left inside a
  // `display:none` tab panel keeps a 0×0 canvas and renders blank/grey until told to re-measure.
  // Whenever the Map tab becomes the active one (including the initial mount, since "map" is the
  // default), force a resize so it's never left stuck at that stale 0-size measurement.
  useEffect(() => {
    if (activeTab !== "map") return;
    // Let the panel's `display: none -> block` flip land before measuring; MapLibre reads the
    // container's CURRENT layout size, so resizing in the same tick (before the browser has
    // recomputed layout for the now-visible panel) can still observe the old 0-size box.
    const raf = requestAnimationFrame(() => mapHandleRef.current?.resize());
    return () => cancelAnimationFrame(raf);
  }, [activeTab]);

  /** List-row/card activation in the stacked List tab must also jump the user straight to the pin
   *  (the key UX this tab view is for) — select it (existing map<->list sync) AND switch back to
   *  the Map tab. Wide/side-by-side width never calls this (no tabs rendered there at all). */
  function selectAndShowMap(ref: string | null): void {
    setSelectedRef(ref);
    setActiveTab("map");
  }

  useEffect(() => {
    let live = true;
    setView(null);
    setLoadError(false);
    setSelectedRef(null);
    listPublicConcerns({
      category: category || undefined,
      from: from || undefined,
      to: to || undefined,
    })
      .then((v) => {
        if (live) setView(v);
      })
      .catch(() => {
        if (live) setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, [category, from, to, reloadKey]);

  // Visible pins = the fetched (category/date-scoped) feed, additionally narrowed to the current
  // status scope — client-side, per the spec (no backend change). Drives BOTH the map and the
  // list/table/cards, so they always stay in lockstep.
  const visiblePins = useMemo(
    () => (view ? filterByStatusScope(view.pins, statusScope) : []),
    [view, statusScope],
  );

  // Live per-scope counts for the segmented control — scoped to category/date filters but ALWAYS
  // ignoring the status filter itself (the "unfiltered-of-this-dimension" convention this component
  // already uses for category options below, applied here to status).
  const statusCounts = useMemo(
    () => ({
      active: view ? filterByStatusScope(view.pins, "active").length : 0,
      resolved: view ? filterByStatusScope(view.pins, "resolved").length : 0,
      all: view ? view.pins.length : 0,
    }),
    [view],
  );

  // Category options are DERIVED from a STABLE, fully unfiltered feed (no category/status/date
  // params at all) — never the scoped/active-filter result, and never a separate admin-categories
  // fetch (which would leak non-showOnMap categories into the filter). Fetched exactly ONCE on
  // mount, regardless of which filters the resident later applies: the public feed applies
  // server-side k-anonymity suppression (mapMinCount), so a status/date filter that narrows the
  // scoped result below the floor returns `pins: []` — deriving categories from THAT would silently
  // collapse the dropdown to just "All categories" even though real showOnMap categories exist
  // citywide (the bug this guards against). If even the unfiltered citywide feed is suppressed, an
  // empty category list is acceptable (the whole map is suppressed anyway).
  const [allCategoryPins, setAllCategoryPins] = useState<PublicConcernPin[] | null>(null);
  useEffect(() => {
    listPublicConcerns({})
      .then((v) => setAllCategoryPins(v.pins))
      .catch(() => {});
  }, []);
  // {value, label} pairs — value is the raw category ID (what `category`/the URL param and the
  // feed filter actually use), label is the human-readable text. Deduped by id, derived from the
  // stable unfiltered feed above. A pin predating `categoryId` falls back to its label as its own
  // id (keeps the dropdown working against an older backend response shape, never crashing).
  const categoryOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const p of allCategoryPins ?? []) {
      const id = p.categoryId ?? p.category;
      if (!byId.has(id)) byId.set(id, p.category);
    }
    // A category selected via deep-link/filter (an ID) that the unfiltered snapshot didn't happen
    // to carry (e.g. race on first load, or it's suppressed there too) still stays selectable — the
    // <select> must never silently drop the current selection. Resolve its label from the snapshot
    // if possible; otherwise fall back to showing the id itself (should be rare).
    if (category && !byId.has(category)) {
      const match = (allCategoryPins ?? []).find((p) => p.categoryId === category);
      byId.set(category, match?.category ?? category);
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allCategoryPins, category]);

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  function resetFilters(): void {
    setParams(new URLSearchParams(), { replace: true });
  }

  const hasFilters = Boolean(category || statusScope !== DEFAULT_STATUS_SCOPE || from || to);

  // "Recently resolved" affordance (spec: render gracefully when suppressed/empty — never assume a
  // non-empty feed). The MOST RECENT resolved pin from the unfiltered feed's own ordering
  // (newest-reported-first — resolved pins keep that same order, so the first resolved match is the
  // most-recently-REPORTED resolved concern, a reasonable proxy without a second server sort key).
  const recentlyResolved = view?.pins.find((p) => p.status === "resolved") ?? null;

  return (
    <ResidentThemeRoot
      ref={rootRef}
      // `.td-intake--wide` (standalone) and `.td-intake--embed-wide` (embed) both lift the
      // inherited 560px `--td-iframe-max-w` cap to `--td-app-max-w` (~1360px): this page is a
      // map+list board, not a narrow intake form (concerns-map-redesign-spec.md), and its layout is
      // container-query driven off `.td-concerns__main`'s OWN rendered width — so the embed must be
      // allowed to grow to the full width of whatever iframe it's actually hosted in (a wide
      // water-project-page embed gets side-by-side map+list; a narrow embed still stacks to cards).
      // Unlike most other embeddable surfaces (intake forms, meetings, FAQ), which are correctly
      // capped at 560px, this page is the one exception — see app.css's `.td-intake--embed-wide`.
      className={`td-intake${embed ? " td-intake--embed td-intake--embed-wide" : " td-intake--wide"}${chromeless ? " td-intake--chromeless" : ""}`}
    >
      {chromeless ? null : (
        <a href="#td-concerns-main" className="td-skip-link">
          Skip to reported concerns
        </a>
      )}
      <TownHeader embed={embed} subcopy={`Reported concerns in ${config.townName}.`} />

      {/*
       * `.td-concerns__main` is the container-query root (`container-type: inline-size`) driving
       * every breakpoint below — the page responds to ITS OWN rendered width, never the browser
       * viewport, because it can be embedded at any width regardless of the host window (ADR-0004).
       * The outer width CAP itself (560px normally, lifted to ~1360px here) is set on the
       * ResidentThemeRoot above via `.td-intake--wide` (standalone) vs. plain `.td-intake` (embed).
       */}
      <main id="td-concerns-main" className="td-intake__main td-concerns td-concerns__main">
        {chromeless ? null : (
          <div className="page-head">
            <div>
              <h1 className="td-intake__title">Reported Concerns</h1>
              <p className="page-head__sub">
                See what residents have reported in {config.townName}, and what's been resolved.
              </p>
            </div>
            <Link to={embed ? "/track?embed=1" : "/track"} className="td-btn td-btn--ghost">
              Look up your report →
            </Link>
          </div>
        )}

        <p className="td-concerns__privacy-note">
          Pin locations are approximate — shifted to protect reporter privacy.
        </p>

        {loadError ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>Couldn't load reported concerns — please try again.</p>
            <button
              type="button"
              className="td-btn td-btn--secondary"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <ConcernsSummary view={view} recentlyResolved={recentlyResolved} />

            <ConcernsFilters
              category={category}
              statusScope={statusScope}
              statusCounts={statusCounts}
              from={from}
              to={to}
              categoryOptions={categoryOptions}
              hasFilters={hasFilters}
              onCategoryChange={(v) => updateParam("category", v)}
              onStatusScopeChange={(v) =>
                updateParam("status", v === DEFAULT_STATUS_SCOPE ? "" : v)
              }
              onFromChange={(v) => updateParam("from", v)}
              onToChange={(v) => updateParam("to", v)}
              onReset={resetFilters}
            />

            {view === null ? (
              <ConcernsSkeleton />
            ) : (
              <>
                {/* Tabs only ever matter at the stacked (<1100px container) width — at >=1100px
                    `.td-concerns__board--tabbed` is overridden back to the plain side-by-side grid
                    and `.td-concerns__tabs` is hidden entirely by the same container query
                    (app.css), so this markup is always present but only VISIBLE/operative when
                    narrow. Rendering it unconditionally (rather than branching in JS on viewport)
                    keeps the page's behavior driven purely by the container query, consistent with
                    every other breakpoint on this page — including inside an embed responding to
                    its own iframe width. */}
                <ConcernsViewTabs activeTab={activeTab} onChange={setActiveTab} />
                <div className="td-concerns__board td-concerns__board--tabbed">
                  <div
                    role="tabpanel"
                    id="td-concerns-tabpanel-map"
                    aria-labelledby="td-concerns-tab-map"
                    className={`td-concerns__panel td-concerns__panel--map${activeTab === "map" ? " is-active" : ""}`}
                  >
                    <ConcernsMap
                      ref={mapHandleRef}
                      tenantId={config.tenantId}
                      pins={visiblePins}
                      selectedRef={selectedRef}
                      onSelect={setSelectedRef}
                      dark={false}
                    />
                  </div>
                  <div
                    role="tabpanel"
                    id="td-concerns-tabpanel-list"
                    aria-labelledby="td-concerns-tab-list"
                    className={`td-concerns__panel td-concerns__panel--list${activeTab === "list" ? " is-active" : ""}`}
                  >
                    <ConcernsList
                      pins={visiblePins}
                      suppressed={view.suppressed}
                      selectedRef={selectedRef}
                      onSelect={selectAndShowMap}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

type ConcernsViewTab = "map" | "list";
const VIEW_TABS: { value: ConcernsViewTab; label: string }[] = [
  { value: "map", label: "Map" },
  { value: "list", label: "List" },
];

/** "Map"/"List" tabs — STACKED (<1100px container) width only; see the board's own comment in
 *  ConcernsMapPage for why this is unconditionally rendered and left to CSS to hide at wide width.
 *  A proper `tablist`/`tab` pair (not `radiogroup`/`radio` — these are PANELS, not a value choice),
 *  arrow-key navigable, mirroring the filled/high-contrast selected treatment already established by
 *  `.td-status-switch__btn[aria-pressed="true"]` above — never a faint outline. */
function ConcernsViewTabs({
  activeTab,
  onChange,
}: {
  activeTab: ConcernsViewTab;
  onChange: (tab: ConcernsViewTab) => void;
}): JSX.Element {
  function moveSelection(delta: 1 | -1): void {
    const i = VIEW_TABS.findIndex((t) => t.value === activeTab);
    const next = VIEW_TABS[(i + delta + VIEW_TABS.length) % VIEW_TABS.length]!;
    onChange(next.value);
  }
  return (
    <div className="td-concerns__tabs" role="tablist" aria-label="Map or list view">
      {VIEW_TABS.map((tab) => {
        const selected = tab.value === activeTab;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={`td-concerns-tab-${tab.value}`}
            aria-selected={selected}
            aria-controls={`td-concerns-tabpanel-${tab.value}`}
            tabIndex={selected ? 0 : -1}
            className={`td-concerns__tab${selected ? " is-active" : ""}`}
            onClick={() => onChange(tab.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                moveSelection(1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                moveSelection(-1);
              }
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function ConcernsSummary({
  view,
  recentlyResolved,
}: {
  view: PublicConcernsView | null;
  recentlyResolved: PublicConcernPin | null;
}): JSX.Element | null {
  if (view === null) return null;
  return (
    <div className="td-concerns__summary">
      <p className="td-concerns__summary-stat" aria-live="polite">
        <strong>{view.summary.reported}</strong> reported · <strong>{view.summary.resolved}</strong>{" "}
        resolved
      </p>
      {recentlyResolved ? (
        <p className="td-concerns__summary-recent">
          Recently resolved: <strong>{recentlyResolved.category}</strong>
          {recentlyResolved.dateResolved ? ` — ${shortDate(recentlyResolved.dateResolved)}` : ""}
        </p>
      ) : null}
    </div>
  );
}

const STATUS_SCOPE_SEGMENTS: { value: StatusScope; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];

/** 3-way segmented control replacing the old status `<select>` (concerns-map-redesign-spec.md).
 *  A proper `radiogroup`/`radio` pair, arrow-key navigable — not just click handlers — so the
 *  current scope is a permanently-visible, keyboard-operable fact of the page's chrome rather than
 *  something hidden until a dropdown opens. */
function StatusSwitch({
  value,
  counts,
  onChange,
}: {
  value: StatusScope;
  counts: { active: number; resolved: number; all: number };
  onChange: (v: StatusScope) => void;
}): JSX.Element {
  function moveSelection(delta: 1 | -1): void {
    const i = STATUS_SCOPE_SEGMENTS.findIndex((s) => s.value === value);
    const next =
      STATUS_SCOPE_SEGMENTS[
        (i + delta + STATUS_SCOPE_SEGMENTS.length) % STATUS_SCOPE_SEGMENTS.length
      ]!;
    onChange(next.value);
  }
  return (
    <div className="td-status-switch" role="radiogroup" aria-label="Status">
      {STATUS_SCOPE_SEGMENTS.map((seg) => {
        const checked = seg.value === value;
        return (
          <button
            key={seg.value}
            type="button"
            className="td-status-switch__btn"
            role="radio"
            aria-checked={checked}
            aria-pressed={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(seg.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                moveSelection(1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                moveSelection(-1);
              }
            }}
          >
            {seg.label} <span className="td-status-switch__count">{counts[seg.value]}</span>
          </button>
        );
      })}
    </div>
  );
}

function ConcernsFilters({
  category,
  statusScope,
  statusCounts,
  from,
  to,
  categoryOptions,
  hasFilters,
  onCategoryChange,
  onStatusScopeChange,
  onFromChange,
  onToChange,
  onReset,
}: {
  category: string;
  statusScope: StatusScope;
  statusCounts: { active: number; resolved: number; all: number };
  from: string;
  to: string;
  categoryOptions: { value: string; label: string }[];
  hasFilters: boolean;
  onCategoryChange: (v: string) => void;
  onStatusScopeChange: (v: StatusScope) => void;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  onReset: () => void;
}): JSX.Element {
  return (
    <form className="td-concerns__filters" role="search" aria-label="Filter reported concerns">
      <label className="td-concerns__filter-label" htmlFor="concerns-category">
        Category
        <select
          id="concerns-category"
          className="td-select"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <div className="td-concerns__filter-label">
        Status
        <StatusSwitch value={statusScope} counts={statusCounts} onChange={onStatusScopeChange} />
      </div>
      <span className="td-concerns__filter-daterange">
        <label className="td-concerns__filter-label" htmlFor="concerns-from">
          From
          <input
            id="concerns-from"
            type="date"
            className="td-input"
            value={from}
            onChange={(e) => onFromChange(e.target.value)}
          />
        </label>
        <label className="td-concerns__filter-label" htmlFor="concerns-to">
          To
          <input
            id="concerns-to"
            type="date"
            className="td-input"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
          />
        </label>
      </span>
      {hasFilters ? (
        <button type="button" className="td-concerns__filter-reset" onClick={onReset}>
          Reset filters
        </button>
      ) : null}
    </form>
  );
}

/** Neutral/uncolored Type chip (concerns-map-redesign-spec.md) — deliberately NOT a colored pill:
 *  Status already owns the one hue-coded signal on this page. Omitted entirely when `type` is
 *  empty/absent (never rendered as a blank chip). The icon swatch is decorative only (aria-hidden) —
 *  the type label text is the accessible name, same convention as the status pill's dot. */
function TypeChip({ type }: { type?: string }): JSX.Element | null {
  if (!type) return null;
  return (
    <span className="td-type-chip">
      <span className="td-type-chip__icon" aria-hidden="true" />
      {type}
    </span>
  );
}

/** Renders BOTH the card list (<720px container width) and the table (≥720px) from the SAME pin
 *  data — CSS container queries (not JS) decide which one is actually visible at any given
 *  container width, guaranteeing the two never drift out of parity (concerns-map-redesign-spec.md).
 *  Row/card selection (tabIndex + Enter/Space) is kept parity-identical across both renderings. */
function ConcernsList({
  pins,
  suppressed,
  selectedRef,
  onSelect,
}: {
  pins: PublicConcernPin[];
  suppressed: boolean;
  selectedRef: string | null;
  onSelect: (ref: string | null) => void;
}): JSX.Element {
  if (suppressed) {
    return (
      <p className="td-muted td-concerns__suppressed">
        Not enough reports in this area yet to show individual pins — check back as more are
        reported.
      </p>
    );
  }
  if (pins.length === 0) {
    return <p className="td-muted">No reported concerns match these filters.</p>;
  }

  function activate(ref: string): void {
    onSelect(ref === selectedRef ? null : ref);
  }
  function onActivateKeyDown(e: KeyboardEvent, ref: string): void {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate(ref);
    }
  }

  return (
    <>
      {/* CARDS — rendered only at < 720px container width (app.css's @container rule). */}
      <div className="td-concerns__cards">
        {pins.map((p) => {
          const isSelected = p.ref === selectedRef;
          return (
            <div
              key={p.ref}
              role="button"
              tabIndex={0}
              className={`td-concern-card${isSelected ? " is-selected" : ""}`}
              aria-selected={isSelected}
              onClick={() => activate(p.ref)}
              onKeyDown={(e) => onActivateKeyDown(e, p.ref)}
            >
              <div className="td-concern-card__top">
                <TypeChip type={p.type} />
                <ConcernStatusPill status={p.status} size="sm" />
              </div>
              <p className="td-concern-card__loc">
                {approxLocation(p)} · {p.category}
              </p>
              <div className="td-concern-card__dates">
                <span>
                  Reported <b>{shortDate(p.dateReported)}</b>
                </span>
                {p.dateResolved ? (
                  <span>
                    Resolved <b>{shortDate(p.dateResolved)}</b>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* TABLE — rendered only at ≥ 720px container width; own horizontal-scroll wrapper so it can
          never clip (the wrapper scrolls, the page body never does). */}
      <div className="td-concerns__table-wrap">
        <table className="td-concerns__table">
          <caption className="td-sr-only">
            Reported concerns — type, approximate location, category, status, and dates
          </caption>
          <thead>
            <tr>
              <th scope="col">Type</th>
              <th scope="col">Location (approx.)</th>
              <th scope="col">Category</th>
              <th scope="col">Status</th>
              <th scope="col">Reported</th>
              <th scope="col">Resolved</th>
            </tr>
          </thead>
          <tbody>
            {pins.map((p) => {
              const isSelected = p.ref === selectedRef;
              return (
                <tr
                  key={p.ref}
                  tabIndex={0}
                  className={`td-concerns__row${isSelected ? " is-selected" : ""}`}
                  aria-selected={isSelected}
                  onClick={() => activate(p.ref)}
                  onKeyDown={(e) => onActivateKeyDown(e, p.ref)}
                >
                  <td>
                    <TypeChip type={p.type} />
                  </td>
                  <td>{approxLocation(p)}</td>
                  <td>{p.category}</td>
                  <td>
                    <ConcernStatusPill status={p.status} size="sm" />
                  </td>
                  <td className="td-mono">{shortDate(p.dateReported)}</td>
                  <td className="td-mono">{p.dateResolved ? shortDate(p.dateResolved) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ConcernsSkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Loading reported concerns…" className="td-skel-rows">
      <Skeleton width="100%" height="320px" />
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="td-skel-row" key={i}>
          <Skeleton width="55%" />
          <Skeleton width="30%" height="var(--td-text-sm)" />
        </div>
      ))}
    </SkeletonRegion>
  );
}
